// ZoneEditor mimics Wansight's "IP Zone Configuration" modal layout: a
// 2-panel split with the prefix tree on the left and per-prefix Settings/
// Comments tabs on the right. Toolbar above the tree exposes Add / Delete
// / Bulk paste / New zone, and the IP Zone selector lives in the same row.
import { useEffect, useMemo, useState } from "react";
import { useRole } from "../contexts/AuthContext";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  api,
  type IPAddrWriteReq,
  type Zone,
  type ZoneNode,
} from "../api";

export default function ZoneEditor() {
  const { isAdmin, isOperator } = useRole();
  const [zones, setZones] = useState<Zone[]>([]);
  const [activeZone, setActiveZone] = useState<number | null>(null);
  const [nodes, setNodes] = useState<ZoneNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedID, setSelectedID] = useState<number | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editNode, setEditNode] = useState<ZoneNode | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [zoneCreateOpen, setZoneCreateOpen] = useState(false);

  async function loadZones() {
    try {
      const zs = await api.zones();
      setZones(zs);
      if (activeZone === null && zs.length > 0) setActiveZone(zs[0].id);
    } catch (e) {
      message.error(String(e));
    }
  }

  async function loadNodes(zoneID: number) {
    setLoading(true);
    try {
      const ns = await api.zoneNodes(zoneID);
      setNodes(ns);
      if (selectedID && !ns.some((n) => n.id === selectedID)) {
        setSelectedID(null);
      }
    } catch (e) {
      message.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeZone != null) loadNodes(activeZone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeZone]);

  const selected = useMemo(
    () => nodes.find((n) => n.id === selectedID) ?? null,
    [nodes, selectedID]
  );

  // ---------------------------------------------------------------------
  // left panel — flat tree (one nesting level via parent_id is supported by
  // the API but the UI keeps it simple and renders all rows as a flat list
  // sorted by prefix; matches what 90% of operators do anyway).
  // ---------------------------------------------------------------------
  const treeColumns: ColumnsType<ZoneNode> = [
    {
      title: "Prefix",
      dataIndex: "prefix",
      render: (v: string, row) => (
        <span style={{ fontFamily: "monospace" }}>
          <span style={{ color: "#a04040", marginRight: 6 }}>▸</span>
          {v}
          {row.description && (
            <span style={{ color: "#888", marginLeft: 8, fontWeight: "normal" }}>
              {row.description}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Card
        size="small"
        title="IP Zone Configuration"
        extra={
          <Space size={6}>
            <span style={{ fontSize: 12, color: "#666" }}>IP Zone:</span>
            <Select
              size="small"
              style={{ width: 200 }}
              placeholder="pick a zone"
              value={activeZone ?? undefined}
              onChange={(v) => setActiveZone(v)}
              options={zones.map((z) => ({
                value: z.id,
                label: `${z.id} — ${z.name}`,
              }))}
            />
            {isOperator && (
              <Button size="small" onClick={() => setZoneCreateOpen(true)}>
                New zone
              </Button>
            )}
          </Space>
        }
        styles={{ body: { padding: 0 } }}
      >
        <Row gutter={0} style={{ minHeight: 480 }}>
          {/* LEFT: prefix tree */}
          <Col
            span={10}
            style={{
              borderRight: "1px solid #d0d0d0",
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                padding: 6,
                background: "#f5f5f5",
                borderBottom: "1px solid #d0d0d0",
              }}
            >
              {isOperator && (
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={activeZone == null}
                  onClick={() => setAddOpen(true)}
                >
                  Add prefix
                </Button>
              )}
              {isOperator && (
                <Button
                  size="small"
                  disabled={activeZone == null}
                  onClick={() => setBulkOpen(true)}
                >
                  Bulk paste
                </Button>
              )}
              {isAdmin && (
                <Popconfirm
                  title={selected ? `Delete ${selected.prefix}?` : "Select a prefix first"}
                  disabled={!selected}
                  onConfirm={async () => {
                    if (activeZone == null || !selected) return;
                    try {
                      await api.deleteZoneNode(activeZone, selected.id);
                      setSelectedID(null);
                      loadNodes(activeZone);
                    } catch (e) {
                      message.error(String(e));
                    }
                  }}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!selected}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </div>
            <Table
              rowKey="id"
              dataSource={nodes}
              columns={treeColumns}
              loading={loading}
              pagination={{ pageSize: 50, size: "small", showSizeChanger: false }}
              size="small"
              showHeader={false}
              rowClassName={(row) =>
                row.id === selectedID ? "ant-table-row-selected" : ""
              }
              onRow={(row) => ({
                onClick: () => setSelectedID(row.id),
                onDoubleClick: () => setEditNode(row),
                style: { cursor: "pointer" },
              })}
              locale={{
                emptyText:
                  "No prefixes — Add prefix or Bulk paste to populate this zone",
              }}
            />
          </Col>

          {/* RIGHT: settings tabs */}
          <Col span={14} style={{ background: "#fff" }}>
            {selected ? (
              <Tabs
                size="small"
                style={{ padding: 8 }}
                items={[
                  {
                    key: "settings",
                    label: "Settings",
                    children: (
                      <SettingsPanel
                        node={selected}
                        canEdit={isOperator}
                        onEdit={() => setEditNode(selected)}
                      />
                    ),
                  },
                  {
                    key: "comments",
                    label: "Comments",
                    children: (
                      <div style={{ padding: 12, color: "#888", fontSize: 12 }}>
                        Comments for {selected.prefix} — not implemented yet.
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <div
                style={{
                  padding: 24,
                  color: "#999",
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                Select a prefix on the left to view settings
              </div>
            )}
          </Col>
        </Row>
      </Card>

      <NodeForm
        open={addOpen || editNode != null}
        onClose={() => {
          setAddOpen(false);
          setEditNode(null);
        }}
        onSaved={() => activeZone != null && loadNodes(activeZone)}
        zoneID={activeZone}
        node={editNode}
      />

      <BulkForm
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => activeZone != null && loadNodes(activeZone)}
        zoneID={activeZone}
      />

      <ZoneCreateForm
        open={zoneCreateOpen}
        onClose={() => setZoneCreateOpen(false)}
        onSaved={(id) => {
          setActiveZone(id);
          loadZones();
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// right-panel content: Settings table mirroring Wansight's parameter grid
// ---------------------------------------------------------------------------
function SettingsPanel({
  node,
  canEdit,
  onEdit,
}: {
  node: ZoneNode;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const rows = [
    {
      key: "ipa",
      param: "IP Accounting",
      value: node.accounting ? "Yes" : "No",
      inh: "None",
      storage: "Up to ~210 MB/day per /16 in MariaDB",
    },
    {
      key: "ipg",
      param: "IP Graphing",
      value: node.graphs ? "Yes" : "No",
      inh: "None",
      storage: "InfluxDB rows scale with prefix size",
    },
  ];
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: "6px 4px",
          color: "#2a2d33",
        }}
      >
        Settings for {node.prefix}{" "}
        {node.description && (
          <span style={{ color: "#888", fontWeight: "normal" }}>
            — {node.description}
          </span>
        )}
      </div>
      <table
        style={{
          width: "100%",
          fontSize: 12,
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr style={{ background: "#efefef" }}>
            <th
              style={{
                textAlign: "left",
                padding: "4px 8px",
                borderBottom: "1px solid #d0d0d0",
              }}
            >
              Parameter
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "4px 8px",
                borderBottom: "1px solid #d0d0d0",
              }}
            >
              Value
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "4px 8px",
                borderBottom: "1px solid #d0d0d0",
              }}
            >
              Inheritance
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "4px 8px",
                borderBottom: "1px solid #d0d0d0",
              }}
            >
              Storage Requirements
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
                {r.param}
              </td>
              <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
                <Tag color={r.value === "Yes" ? "green" : undefined}>
                  {r.value}
                </Tag>
              </td>
              <td
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid #eee",
                  color: "#888",
                }}
              >
                {r.inh}
              </td>
              <td
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid #eee",
                  color: "#888",
                }}
              >
                {r.storage}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div style={{ textAlign: "right", marginTop: 12 }}>
          <Button size="small" type="primary" onClick={onEdit}>
            Update
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// nested modal: create or edit a single ipaddr row
// ---------------------------------------------------------------------------

function NodeForm({
  open,
  onClose,
  onSaved,
  zoneID,
  node,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  zoneID: number | null;
  node: ZoneNode | null;
}) {
  const [form] = Form.useForm<IPAddrWriteReq>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (node) {
      form.setFieldsValue({
        prefix: node.prefix,
        description: node.description,
        graphs: node.graphs,
        accounting: node.accounting,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ graphs: true, accounting: true });
    }
  }, [open, node, form]);

  async function onOk() {
    if (zoneID == null) return;
    try {
      const v = await form.validateFields();
      setBusy(true);
      if (node) {
        await api.updateZoneNode(zoneID, node.id, v);
      } else {
        await api.createZoneNode(zoneID, v);
      }
      message.success(node ? "Updated" : "Created");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={node ? `Edit ${node.prefix}` : "Add prefix"}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Save"
    >
      <Form<IPAddrWriteReq> form={form} layout="vertical">
        <Form.Item
          name="prefix"
          label="CIDR prefix"
          rules={[{ required: true, message: "e.g. 10.0.0.0/8 or 2001:db8::/32" }]}
        >
          <Input placeholder="10.0.0.0/8" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input placeholder="customer name, region, …" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="graphs"
              label="Graphs"
              valuePropName="checked"
              extra="store per-IP time series in InfluxDB"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="accounting"
              label="Accounting"
              valuePropName="checked"
              extra="include in flow aggregates"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// nested modal: bulk insert CIDR list
// ---------------------------------------------------------------------------

function BulkForm({
  open,
  onClose,
  onSaved,
  zoneID,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  zoneID: number | null;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  async function onOk() {
    if (zoneID == null) return;
    const prefixes = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s && !s.startsWith("#"));
    if (prefixes.length === 0) {
      message.warning("No prefixes to insert");
      return;
    }
    setBusy(true);
    try {
      const r = await api.bulkZoneNodes(zoneID, prefixes);
      message.success(`Inserted ${r.inserted} prefixes`);
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Bulk paste CIDR list"
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Insert"
      width={620}
    >
      <p style={{ color: "#888" }}>
        One CIDR per line. Lines starting with <code>#</code> are ignored.
        All inserted rows default to graphs=on, accounting=on.
      </p>
      <Input.TextArea
        rows={14}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"10.0.0.0/8\n172.16.0.0/12\n192.168.0.0/16\n# comment line"}
        style={{ fontFamily: "monospace" }}
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// nested modal: create a new IP zone
// ---------------------------------------------------------------------------

function ZoneCreateForm({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (newID: number) => void;
}) {
  const [form] = Form.useForm<{ name: string; has_ipv6: boolean }>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ has_ipv6: false });
    }
  }, [open, form]);

  async function onOk() {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const r = await api.createZone(v.name, v.has_ipv6);
      message.success("Zone created");
      onSaved(r.id);
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="New IP Zone"
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Create"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="default, datacenter-hn, …" />
        </Form.Item>
        <Form.Item name="has_ipv6" label="IPv6" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
